import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { ApiService, LocalDate } from '../api/api.service';
import {
  OtherItem,
  ShoplistCategoryItem,
  ShoplistItem,
  GetShoplist$Response,
  GetShoplistOptions$Response,
  CreateShoplist$Response,
  DeactivateShoplist$Response,
  AddOtherItem$Request,
  AddOtherItem$Response,
  RemoveOtherItem$Request,
  RemoveOtherItem$Response,
  CheckShoplistIngredient$Request,
  CheckShoplistIngredient$Response,
  CheckOtherItem$Request,
  CheckOtherItem$Response,
  AddProfileToMeal$Request,
  AddProfileToMeal$Response,
  RemoveProfileFromMeal$Request,
  RemoveProfileFromMeal$Response,
  ShareShoppingList$Request,
  ShareShoppingList$Response
} from '@api/types';

import { map, tap } from 'rxjs/operators';

@Injectable()
export class ShoplistService {
  private defaultState: GetShoplist$Response = {
    shoplist: {
      active: null,
      createdAt: null,
      dates: null,
      id: null,
      otherItems: [],
      shoplistCategoryItems: []
    },
    status: null
  };
  private state: GetShoplist$Response = this.defaultState;
  private shoplistSubject: Subject<GetShoplist$Response> = new BehaviorSubject<GetShoplist$Response>(this.state);

  private isLoading: boolean;

  constructor(private api: ApiService) {}

  setState(newState: GetShoplist$Response | RemoveOtherItem$Response) {
    this.state = { ...this.state, ...newState };
    this.shoplistSubject.next(this.state);
  }

  getState() {
    return this.shoplistSubject;
  }

  getShoplist(forceFetch: boolean = false): Observable<GetShoplist$Response> {
    if (forceFetch || !this.state.status) {
      if (!this.isLoading) {
        this.getShoplistLatest().subscribe();
      }
    }
    return this.shoplistSubject;
  }

  getShoplistLatest() {
    this.isLoading = true;
    return this.api.shoplist.getShoplist().pipe(
      tap(shoplist => this.setState(shoplist)),
      tap(() => (this.isLoading = false))
    );
  }

  createShoplist(dates: string[]): Observable<CreateShoplist$Response> {
    return this.api.shoplist.createShoplist({ dates });
  }

  deactivateShoplist(): Observable<DeactivateShoplist$Response> {
    this.setState(this.defaultState);
    return this.api.shoplist.deactivateShoplist();
  }

  addOtherItem(request: AddOtherItem$Request): Observable<AddOtherItem$Response> {
    return this.api.shoplist.addOtherItem(request).pipe(tap(shoplist => this.setState(shoplist)));
  }

  removeOtherItem(request: RemoveOtherItem$Request): Observable<RemoveOtherItem$Response> {
    return this.api.shoplist.removeOtherItem(request).pipe(tap(shoplist => this.setState(shoplist)));
  }

  checkShoplistIngredient(request: CheckShoplistIngredient$Request): Observable<CheckShoplistIngredient$Response> {
    if (request.ingredientId) {
      this.updateShoplistIngredient(request);
    }
    return this.api.shoplist.checkShoplistIngredient(request).pipe(
      tap(() => {
        if (request.ingredientIds) {
          this.getShoplistLatest().subscribe();
        }
      })
    );
  }

  checkOtherItem(request: CheckOtherItem$Request): Observable<CheckOtherItem$Response> {
    this.updateShoplistOther(request);
    return this.api.shoplist.checkOtherItem(request);
  }

  shareShoppingList(request: ShareShoppingList$Request): Observable<ShareShoppingList$Response> {
    return this.api.shoplist.shareShoppingList(request);
  }

  /* Creating shoplist */

  addProfileToMeal(request: AddProfileToMeal$Request): Observable<AddProfileToMeal$Response> {
    return this.api.meal.addProfileToMeal(request);
  }

  removeProfileFromMeal(request: RemoveProfileFromMeal$Request): Observable<RemoveProfileFromMeal$Response> {
    return this.api.meal.removeProfileFromMeal(request);
  }

  getShoplistOptions(): Observable<GetShoplistOptions$Response> {
    return this.api.shoplist.getShoplistOptions();
  }

  /* Helpers */

  updateShoplistIngredient(request: CheckShoplistIngredient$Request) {
    const shoplistCategoryItems = this.state.shoplist.shoplistCategoryItems.map((shoplistCategoryItem: ShoplistCategoryItem) => {
      const shoplistItems = shoplistCategoryItem.shoplistItems.map((shoplistItem: ShoplistItem) => {
        const checked = request.ingredientId === shoplistItem.ingredient.id ? request.checked : shoplistItem.checked;
        return { ...shoplistItem, checked };
      });
      return { ...shoplistCategoryItem, shoplistItems };
    });
    const shoplist = { ...this.state.shoplist, shoplistCategoryItems };
    this.setState({ shoplist });
  }

  updateShoplistOther(request: CheckOtherItem$Request) {
    const otherItems = this.state.shoplist.otherItems.map((otherItem: OtherItem) => {
      const checked = request.itemId === otherItem.id ? request.checked : otherItem.checked;
      return { ...otherItem, checked };
    });
    const shoplist = { ...this.state.shoplist, otherItems };
    this.setState({ shoplist });
  }
}
